// Data hooks of the Files tab: the generated reads, plus the three writes that carry `If-Match`
// (folder PATCH and DELETE, ACL PUT: the generated mutations cannot send the header) and one
// refresh that brings every folder view of a space (tree, open folder, ACL) up to date.
import {
  getFoldersControllerReplaceAclMutationKey,
  getFoldersControllerUpdateMutationKey,
  useFoldersControllerAcl,
  useFoldersControllerCreate,
  useFoldersControllerGet,
  useFoldersControllerList,
  useMembersControllerList,
  type FolderAclDtoOutput,
  type FolderDtoOutput,
  type PutFolderAclDto,
  type UpdateFolderDto,
} from '@batiflow/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntime } from '@/app/providers/runtime-provider';
import { PICKER_SIZE, etagOf } from './model';

/** The readable children of a folder (the root folders when `parentId` is null), by name. */
export function useFolderChildren(spaceId: string, parentId: string | null, enabled = true) {
  return useFoldersControllerList(spaceId, parentId ? { parentId } : undefined, {
    query: { enabled },
  });
}

/** One folder with its breadcrumbs. */
export function useFolder(spaceId: string, folderId: string | null) {
  return useFoldersControllerGet(spaceId, folderId ?? '', {
    query: { enabled: folderId !== null },
  });
}

/** The custom permissions of a folder (`can_manage`). */
export function useFolderAcl(spaceId: string, folderId: string) {
  return useFoldersControllerAcl(spaceId, folderId);
}

export const useCreateFolder = useFoldersControllerCreate;

/** The organisation's active members, to pick the people of a custom ACL. */
export function useOrganisationMembers(orgId: string) {
  return useMembersControllerList(orgId, { status: 'active', limit: PICKER_SIZE });
}

/** Refreshes every folder query of the space: lists (tree, open folder), details and ACLs. */
export function useRefreshFolders(spaceId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  const prefix = `/api/v1/spaces/${spaceId}/folders`;
  return () =>
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [key] = query.queryKey;
        return typeof key === 'string' && key.startsWith(prefix);
      },
    });
}

export interface UpdateFolderVariables {
  folderId: string;
  /** `version` of the folder as loaded: sent as `If-Match: W/"<version>"`. */
  version: number;
  data: UpdateFolderDto;
}

/** Folder PATCH (rename and/or move) through the client-core fetcher, with `If-Match`. */
export function useUpdateFolder(spaceId: string) {
  const { fetcher } = useRuntime();
  return useMutation({
    mutationKey: getFoldersControllerUpdateMutationKey(),
    mutationFn: async ({ folderId, version, data }: UpdateFolderVariables) => {
      const response = await fetcher<FolderDtoOutput>({
        method: 'PATCH',
        url: `/api/v1/spaces/${spaceId}/folders/${folderId}`,
        data,
        etag: etagOf(version),
      });
      return response.data;
    },
  });
}

/** Folder DELETE (the folder and its subtree go to the trash), with `If-Match`. */
export function useDeleteFolder(spaceId: string) {
  const { fetcher } = useRuntime();
  return useMutation({
    mutationKey: ['folders', 'delete', spaceId],
    mutationFn: async ({ folderId, version }: { folderId: string; version: number }) => {
      await fetcher<unknown>({
        method: 'DELETE',
        url: `/api/v1/spaces/${spaceId}/folders/${folderId}`,
        etag: etagOf(version),
      });
    },
  });
}

export interface ReplaceAclVariables {
  folderId: string;
  /** `version` returned by the ACL read. */
  version: number;
  data: PutFolderAclDto;
}

/** ACL PUT (inherit or the full custom list), with `If-Match`. */
export function useReplaceFolderAcl(spaceId: string) {
  const { fetcher } = useRuntime();
  return useMutation({
    mutationKey: getFoldersControllerReplaceAclMutationKey(),
    mutationFn: async ({ folderId, version, data }: ReplaceAclVariables) => {
      const response = await fetcher<FolderAclDtoOutput>({
        method: 'PUT',
        url: `/api/v1/spaces/${spaceId}/folders/${folderId}/acl`,
        data,
        etag: etagOf(version),
      });
      return response.data;
    },
  });
}
